import { useState } from 'react';
import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';
import { MessageCircle, ArrowRight, Sparkles } from 'lucide-react';

export const WelcomeModal = ({ isOpen, onClose, userName, onStartJourney }) => {
  const [step, setStep] = useState(0);
  
  const firstName = userName?.split(' ')[0] || 'there';

  const steps = [
    {
      title: `Welcome, ${firstName}!`,
      content: (
        <div className="space-y-4">
          <p className="text-muted-foreground">
            I'm so glad you're here. ClarityPath is your space to find direction, 
            build clarity, and grow into the person you want to become.
          </p>
          <p className="text-muted-foreground">
            You're not alone on this journey — I'm here to help.
          </p>
        </div>
      ),
      cta: "Tell me more"
    },
    {
      title: "Meet CLaiR",
      content: (
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-primary/5 rounded-xl">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#1a5f7a] to-[#2d8a8a] flex items-center justify-center flex-shrink-0">
              <MessageCircle className="text-white" size={24} />
            </div>
            <div>
              <p className="font-medium">CL<span className="text-primary">ai</span>R</p>
              <p className="text-sm text-muted-foreground">Your AI clarity coach</p>
            </div>
          </div>
          <p className="text-muted-foreground">
            CLaiR is here whenever you need to talk things through, work on a goal, 
            or just need someone to listen. She remembers your journey and grows with you.
          </p>
        </div>
      ),
      cta: "What can I do here?"
    },
    {
      title: "Your Clarity Toolkit",
      content: (
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-accent text-sm">1</span>
            </div>
            <div>
              <p className="font-medium">Journal</p>
              <p className="text-sm text-muted-foreground">Reflect on your days and track your emotional patterns</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-accent text-sm">2</span>
            </div>
            <div>
              <p className="font-medium">Goals</p>
              <p className="text-sm text-muted-foreground">Set meaningful goals and track your progress step by step</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-accent text-sm">3</span>
            </div>
            <div>
              <p className="font-medium">Insights</p>
              <p className="text-sm text-muted-foreground">Discover patterns and celebrate your growth over time</p>
            </div>
          </div>
        </div>
      ),
      cta: "Let's get started!"
    }
  ];

  const currentStep = steps[step];
  const isLastStep = step === steps.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      onStartJourney();
    } else {
      setStep(step + 1);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden" data-testid="welcome-modal">
        {/* Header with gradient */}
        <div className="bg-gradient-to-br from-primary/10 to-accent/10 p-6 pb-4">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="text-primary" size={20} />
            <span className="text-sm font-medium text-primary">Welcome to ClarityPath</span>
          </div>
          <h2 className="text-2xl font-serif">{currentStep.title}</h2>
        </div>
        
        {/* Content */}
        <div className="p-6 pt-4">
          {currentStep.content}
        </div>
        
        {/* Footer */}
        <div className="p-6 pt-0 flex items-center justify-between">
          <button 
            onClick={handleSkip}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="skip-onboarding"
          >
            I've got this
          </button>
          
          <div className="flex items-center gap-4">
            {/* Progress dots */}
            <div className="flex gap-1.5">
              {steps.map((_, i) => (
                <div 
                  key={`dot-${i}`}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    i === step ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              ))}
            </div>
            
            <Button 
              onClick={handleNext}
              className="rounded-full gap-2"
              data-testid="onboarding-next"
            >
              {currentStep.cta}
              <ArrowRight size={16} />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
